import { useState, useEffect, useRef, useMemo } from 'react'


import { useAppDispatch, useAppSelector } from '@App/hooks'
import useWatchlists from '@App/hooks/useWatchList'
import { IConfigMarket } from '@App/redux/interfaces'
import { configFavoriteMarkets } from '@App/redux/slices/config/config.selectors'
import { addFavoriteMarket, removeFavoriteMarket } from '@App/redux/slices/config/ConfigSlice'
import { MODAL_TYPES } from '@App/redux/slices/modals/enums'
import { setModal } from '@App/redux/slices/modals/ModalSlice'
import { AddIcon } from '@App/static/icons/SpotOrderIcons'
import { MonitorChartIcon } from '@App/static/icons/WalletPageIcons'
import { PopoverMenu, PopoverMenuItem } from '@App/ui/crypto_exchange/desktop/components/common/PopOver'
import { Button } from '@App/ui/crypto_exchange/desktop/components/controls/Button'
import { Checkbox } from '@App/ui/crypto_exchange/desktop/components/controls/Checkbox'

import s from './styles.module.scss'

enum ViewState {
  Default = 1,
  AddToList = 2,
}

export const AddToWatchlist = ({ market, onClose }: { market: IConfigMarket; onClose?: () => void }) => {
  const [viewState, setViewState] = useState<ViewState>(ViewState.Default)

  const favoriteMarkets = useAppSelector(configFavoriteMarkets)
  const isAddedToChart = favoriteMarkets.find((m) => m.id === market.id)
  const dispatch = useAppDispatch()

  const handleOpenAddToList = () => {
    setViewState(ViewState.AddToList)
  }

  const chartText = isAddedToChart ? 'Remove from Charts' : 'Add to Charts'

  const handleToggleChart = () => {
    if (!isAddedToChart) {
      dispatch(addFavoriteMarket(market))
    } else {
      dispatch(removeFavoriteMarket(market.title))
    }
  }

  return (
    <>
      {viewState === ViewState.Default && (
        <PopoverMenu>
          <PopoverMenuItem className={s.item} onClick={handleToggleChart}>
            <MonitorChartIcon className={s.icon} width={20} height={20} />
            {chartText}
          </PopoverMenuItem>
          <PopoverMenuItem className={s.item} onClick={handleOpenAddToList}>
            <AddIcon width={16} />
            Add to list
          </PopoverMenuItem>
        </PopoverMenu>
      )}

      {viewState === ViewState.AddToList && (
        <AddToWatchlistSelector markets={[market]} onAddNewWatchlist={onClose} onSave={onClose} />
      )}
    </>
  )
}
export const AddToWatchlistSelector = ({
  markets,
  onAddNewWatchlist,
  onSave,
}: {
  markets: IConfigMarket[]
  onAddNewWatchlist?: () => void
  onSave?: () => void
}) => {
  const [selectedLists, setSelectedLists] = useState<Set<string>>(new Set())
  const [initialLists, setInitialLists] = useState<Set<string>>(new Set())
  const dispatch = useAppDispatch()
  const isInitialized = useRef(false)

  const { customWatchlists, isMarketInWatchlist, addMarket, removeMarket, getWatchlist } = useWatchlists()

  const favouritesWatchlist = getWatchlist('favourites')

  const displayWatchlists = [
    ...(favouritesWatchlist ? [favouritesWatchlist] : []),
    ...customWatchlists.filter((w) => w.key !== 'favourites'),
  ]

  const marketIds = markets.map((m) => m.id)

  const isAnyMarketInList = (listId: string) => marketIds.some((marketId) => isMarketInWatchlist(listId, marketId))

  const initSelectedLists = () => {
    const initial = new Set<string>()

    displayWatchlists.forEach((w) => {
      if (isAnyMarketInList(w.key)) {
        initial.add(w.key)
      }
    })

    setSelectedLists(initial)
    setInitialLists(initial)
  }

  useEffect(() => {
    if (!isInitialized.current) {
      initSelectedLists()
      isInitialized.current = true
    }
  }, [])
  const hasChanges = useMemo(() => {
    if (selectedLists.size !== initialLists.size) return true
    for (const id of selectedLists) {
      if (!initialLists.has(id)) return true
    }
    return false
  }, [selectedLists, initialLists])
  const handleToggleList = (listId: string) => {
    setSelectedLists((prev) => {
      const next = new Set(prev)
      next.has(listId) ? next.delete(listId) : next.add(listId)
      return next
    })
  }

  const syncList = (listId: string) => {
    const shouldBeInList = selectedLists.has(listId)

    marketIds.forEach((marketId) => {
      const isInList = isMarketInWatchlist(listId, marketId)

      if (shouldBeInList && !isInList) {
        addMarket(listId, marketId)
      }

      if (!shouldBeInList && isInList) {
        removeMarket(listId, marketId)
      }
    })
  }

  const handleSave = () => {
    displayWatchlists.forEach((w) => syncList(w.key))
    customWatchlists.forEach((w) => syncList(w.key))
    onSave?.()
  }

  const handleAddNewWatchlist = () => {
    dispatch(
      setModal({
        modalType: MODAL_TYPES.ADD_WATCHLIST,
        useStack: true,
        data: {
          marketIds,
        },
      }),
    )
    onAddNewWatchlist?.()
  }

  return (
    <div className={s.addToListContainer}>
      <div className={s.addToListHeader}>
        <span className={s.addToListTitle}>Add to list</span>
      </div>

      <ul className={s.watchlistsList}>
        {displayWatchlists.map((list) => (
          <li key={list.key} className={s.watchlistItem} onClick={() => handleToggleList(list.key)}>
            <span className={s.watchlistName}>{list.name}</span>
            <Checkbox name="" checked={selectedLists.has(list.key)} />
          </li>
        ))}
      </ul>

      <div className={s.addToListActions}>
        <Button variant="outlined" className={s.addNewBtn} onClick={handleAddNewWatchlist}>
          Add to new watchlist
        </Button>
        <Button className={s.saveBtn} onClick={handleSave} disabled={!hasChanges}>
          Save
        </Button>
      </div>
    </div>
  )
}
